import React from "react";
import {BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid} from "recharts";
import {CircleHelp, Download, ChevronDown} from "lucide-react";
import {Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {useIsMobile} from "@/hooks/use-mobile";
import {ChartContainer} from "@/components/ui/chart";
import {cn} from "@/lib/utils";
import axiosInstance from "@/api/axios";
import {useTranslation} from "react-i18next";
import {Button} from "@/components/ui/button";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "@/components/ui/dropdown-menu";
import {AiOutlineArrowDown} from 'react-icons/ai'
import {AiOutlineArrowUp} from 'react-icons/ai'
import {getColorScheme} from '@/lib/colorSchemes';

// Normalise a translated/display stream name back to a known key
const getStreamKey = (streamName) => {
    if (!streamName) return null;
    const lower = streamName.toLowerCase();
    if (lower === 'pmd') return 'PMD';
    if (lower.includes('paper') || lower.includes('cardboard')) return 'Paper';
    if (lower.includes('organic') || lower.includes('bio')) return 'Organic';
    if (lower.includes('residual') || lower.includes('rest')) return 'Residual';
    if (lower.includes('glass')) return 'Glass';
    return null;
};

// Convert hex to [h, s, l]
const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
            default: break;
        }
    }
    return [h * 360, s * 100, l * 100];
};

const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash);
};

export const SubclassDisplayChart = React.forwardRef(({
                                                          className,
                                                          id,
                                                          binTypes = {},
                                                          name,
                                                          wasteitem,
                                                          dayvalue,
                                                          datevalue = [{
                                                              startDate: null,
                                                              endDate: null,
                                                              key: 'selection'
                                                          }],
                                                          totalwasteproduceddata,
                                                          wasteweight,
                                                          colorwastechart,
                                                          percentagechangewastechart,
                                                          isCustomDateRange,
                                                          ...props
                                                      }, ref) => {
    const {t} = useTranslation();
    const isMobile = useIsMobile();
    const [processedData, setProcessedData]       = React.useState([]);
    const [categories, setCategories]             = React.useState([]);
    const [loading, setLoading]                   = React.useState(true);
    const [error, setError]                       = React.useState(null);
    const [hasData, setHasData]                   = React.useState(false);
    const [wasteTypeWeights, setWasteTypeWeights] = React.useState({});

    // Fixed color map built once from /waste_classifications/
    // colorMap:           displayName → hsl color string
    // subclassOwnStream:  displayName → streamKey (the subclass's OWN stream, not the bar it's in)
    const [colorMap, setColorMap]                     = React.useState({});
    const [subclassOwnStreamMap, setSubclassOwnStreamMap] = React.useState({});

    const internalChartRef = React.useRef(null);
    React.useImperativeHandle(ref, () => internalChartRef.current);

    const chartTitle = t('subwastecompositionlabel');

    // ── Resolve the country-based color scheme for the selected bins ─────────
    // Seeds every subclass shade below (getSubclassColor), same non-inverted
    // scheme as the other dashboard charts (ContaminationChart, Stats, etc).
    const binIdList = React.useMemo(() => {
        if (Array.isArray(id)) return id;
        if (typeof id === 'string') return id.split(',').map(s => s.trim()).filter(Boolean);
        return id ? [id] : [];
    }, [id]);

    const streamBaseColors = React.useMemo(() => {
        const scheme = getColorScheme(binIdList, binTypes);
        return {
            PMD:      scheme.PMD,
            Organic:  scheme.Organic,
            Paper:    scheme.Paper,
            Residual: scheme.Residual,
        };
    }, [binIdList, binTypes]);

    // Generate a shade of the stream base color for a subclass.
    // Color always follows what the subclass IS, not which bar it sits in.
    const getSubclassColor = React.useCallback((subclassName, streamKey) => {
        if (!subclassName || !streamKey) return '#CCCCCC';

        const baseHex = streamBaseColors[streamKey];
        if (!baseHex) return '#CCCCCC';

        const [baseH, baseS, baseL] = hexToHsl(baseHex);
        const hash = hashCode(subclassName.toLowerCase());

        const lOffset = ((hash % 13) - 6) * 3;           // -18 to +18
        const newL    = Math.min(72, Math.max(28, baseL + lOffset));
        const hOffset = ((hash >> 4) % 17) - 8;           // -8 to +8 degrees
        const newH    = (baseH + hOffset + 360) % 360;
        const sOffset = ((hash >> 8) % 17) - 8;           // -8 to +8 %
        const newS    = Math.min(100, Math.max(30, baseS + sOffset));

        return `hsl(${newH.toFixed(0)}, ${newS.toFixed(0)}%, ${newL.toFixed(0)}%)`;
    }, [streamBaseColors]);

    // ── Helpers ──────────────────────────────────────────────────────────────

    const shouldFilterWasteType = (wasteType) => {
        if (!wasteType) return true;
        const normalized = wasteType.toLowerCase().trim();
        return normalized === 'nothing' || normalized === '';
    };

    const normalizeAndCombineWasteTypes = (name) => {
        if (!name) return '';
        const normalized = name.toLowerCase().replace(/[_-]/g, '').trim();
        if (normalized === 'nothing') return '';

        const combinedMap = {
            'pmd':            'PMD',
            'residual':       t('residuallabel') || 'Residual',
            'rest':           t('residuallabel') || 'Residual',
            'papercardboard': t('paperlabel') || 'Paper/Cardboard',
            'organic':        t('organiclabel') || 'Organic',
            'glass':          'Glass'
        };

        return combinedMap[normalized] ||
            name.replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
                .replace(/[_-]/g, ' ')
                .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
                .replace(/\s+/g, ' ')
                .trim();
    };

    const normalizeAndFormatSubclassName = (name) => {
        if (!name) return '';

        const transformations = {
            'pmd':                   'PMD',
            'rest':      t('residuallabel') || 'Residual',   
            'residual':  t('residuallabel') || 'Residual',  
            'organic':   t('organiclabel')  || 'Organic',    
            'paper':     t('paperlabel')    || 'Paper',   
            'plasticbottle':         t('plasticBottle'),
            'metalcan':              t('metalCan'),
            'plasticbag':            t('plasticBag'),
            'plasticbagcolor':       t('plasticBagColor'),
            'organicmandarin':       t('organicMandarin'),
            'paperbox':              t('paperBox'),
            'papercarton':           t('paperCarton'),
            'papercolor':            t('paperColor'),
            'papertissue':           t('paperTissue'),
            'paperoffice':           t('paperOffice'),
            'plasticbagwrapper':     t('plasticBagWrapper'),
            'plasticcontainer':      t('plasticContainer'),
            'drinkcarton':           t('drinkCarton'),
            'juicepackage':          t('juicePackage'),
            'rubbergloves':          t('rubberGloves'),
            'banana':                t('banana'),
            'apple':                 t('apple'),
            'papercoffeecup':        t('paperCoffeeCup'),
            'plasticbagsilver':      t('plasticBagSilver'),
            'plasticbagtransparent': t('plasticBagTransparent', 'Plastic Bag Transparent'),
            'bottlecap':             t('bottleCap', 'Bottle Cap'),
        };
        const lowerName = name.toLowerCase().replace(/[_-]/g, '');
        return transformations[lowerName] ||
            name.replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
                .replace(/[_-]/g, ' ')
                .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
                .replace(/\s+/g, ' ')
                .trim();
    };

    // ── Fetch fixed color map from WasteClassification DB table ─────────────
    // Colors are keyed by subclass identity, not which stream/bar they appear in.
    // A plastic bottle showing up in the Organic bar will still be PMD-colored.
    React.useEffect(() => {
        axiosInstance.get('/waste_classifications/').then(res => {
            const map = {};
            const ownStreamMap = {};
            res.data.forEach(({ main_class, subclass }) => {
                const streamKey   = getStreamKey(main_class);
                if (!streamKey) return;
                const displayName = normalizeAndFormatSubclassName(subclass);
                if (displayName) {
                    // If subclass name matches the stream itself, use exact base color
                    const isMainSubclass = displayName.toUpperCase() === streamKey.toUpperCase()
                        || displayName.toLowerCase() === 'residual' && streamKey === 'Residual'
                        || displayName.toLowerCase() === 'organic'  && streamKey === 'Organic'
                        || displayName.toLowerCase() === 'paper'    && streamKey === 'Paper';

                    map[displayName]          = isMainSubclass
                        ? streamBaseColors[streamKey]
                        : getSubclassColor(displayName, streamKey);
                    ownStreamMap[displayName] = streamKey;
                }
            });
            setColorMap(map);
            setSubclassOwnStreamMap(ownStreamMap);
        }).catch(err => console.error('Failed to load waste classifications:', err));
    }, [t, streamBaseColors, getSubclassColor]);

    // ── chartConfig ──────────────────────────────────────────────────────────
    const chartConfig = React.useMemo(() => {
        const config = {};

        if (processedData.length > 0 && hasData) {
            Object.keys(processedData[0]).forEach((key) => {
                if (key !== 'name') {
                    const displayName = normalizeAndFormatSubclassName(key);

                    // The subclass's OWN stream from the DB (used only as fallback)
                    const ownStreamKey = subclassOwnStreamMap[displayName] || null;

                    config[key] = {
                        label:       displayName,
                        // 1. Fixed DB-derived color (preferred)
                        // 2. Fallback: compute from subclass's own stream (not the bar's stream)
                        color:       colorMap[displayName] || getSubclassColor(displayName, ownStreamKey),
                        originalKey: key,
                    };
                }
            });
        }

        return config;
    }, [processedData, hasData, colorMap, subclassOwnStreamMap, t, getSubclassColor]);

    // ── Fetch chart data ─────────────────────────────────────────────────────
    React.useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                setHasData(false);

                let binIdentifiers;
                if (Array.isArray(id)) {
                    binIdentifiers = id;
                } else if (typeof id === 'string' && id.includes(',')) {
                    binIdentifiers = id.split(',');
                } else {
                    binIdentifiers = [id];
                }

                binIdentifiers = binIdentifiers
                    .map(id => typeof id === 'string' ? id.trim() : String(id))
                    .filter(id => id.length > 0);

                if (binIdentifiers.length === 0) {
                    throw new Error('No valid bin identifiers provided');
                }

                const binResponse = await axiosInstance.get('/get_bins');
                const validBins = binIdentifiers.map(identifier => {
                    let bin = binResponse.data.find(b =>
                        String(b.unique_identifier).toLowerCase() === String(identifier).toLowerCase()
                    );
                    if (!bin && !isNaN(identifier)) {
                        bin = binResponse.data.find(b => b.id === parseInt(identifier));
                    }
                    return bin;
                }).filter(bin => bin !== undefined && bin !== null);

                if (validBins.length === 0) {
                    throw new Error(`No bins found matching identifiers: ${binIdentifiers.join(', ')}`);
                }

                const binIds = validBins.map(bin => bin.id).join(',');

                let apiUrl = `/subclass_waste_composition/${binIds}/${dayvalue.toLowerCase()}/`;
                const queryParams = new URLSearchParams();

                if (isCustomDateRange && datevalue[0] && datevalue[0].startDate && datevalue[0].endDate) {
                    const rawStartDate = datevalue[0].startDate;
                    const rawEndDate   = datevalue[0].endDate;
                    let startDateStr, endDateStr;

                    startDateStr = rawStartDate instanceof Date
                        ? rawStartDate.toISOString()
                        : typeof rawStartDate === 'string'
                            ? rawStartDate
                            : new Date(rawStartDate).toISOString();

                    endDateStr = rawEndDate instanceof Date
                        ? rawEndDate.toISOString()
                        : typeof rawEndDate === 'string'
                            ? rawEndDate
                            : new Date(rawEndDate).toISOString();

                    queryParams.append('start_date', startDateStr);
                    queryParams.append('end_date', endDateStr);
                    console.log('SubclassChart: Using custom date range', startDateStr, endDateStr);
                }

                if (queryParams.toString()) {
                    apiUrl += `?${queryParams.toString()}`;
                }

                console.log('SubclassChart: Fetching data from', apiUrl);
                const response = await axiosInstance.get(apiUrl);

                if (!response.data || Object.keys(response.data).length === 0) {
                    setProcessedData([]);
                    setCategories([]);
                    setHasData(false);
                    return;
                }

                const combinedData = {};

                Object.keys(response.data).forEach(wasteType => {
                    if (shouldFilterWasteType(wasteType)) return;

                    const normalizedType = normalizeAndCombineWasteTypes(wasteType);
                    if (!normalizedType) return;

                    if (!combinedData[normalizedType]) {
                        combinedData[normalizedType] = {};
                    }

                    Object.keys(response.data[wasteType]).forEach(subClass => {
                        if (shouldFilterWasteType(subClass)) return;

                        const normalizedSubClass = normalizeAndFormatSubclassName(subClass);
                        const raw = response.data[wasteType][subClass];

                        // subclass_waste_composition now returns
                        // { weight, clean_weight, contaminated_weight } per
                        // subclass instead of a bare number (added for the
                        // treemap contamination badges) — this chart only
                        // needs the total weight, so unwrap it here. Falls
                        // back to a bare number for safety if the API shape
                        // ever changes back.
                        const value = typeof raw === 'object' && raw !== null ? (raw.weight ?? 0) : (raw ?? 0);

                        if (normalizedSubClass && value > 0) {
                            combinedData[normalizedType][normalizedSubClass] =
                                (combinedData[normalizedType][normalizedSubClass] || 0) + value;
                        }
                    });
                });

                const filteredCombinedData = {};
                Object.keys(combinedData).forEach(type => {
                    const typeData = combinedData[type];
                    const hasValidSubclasses = Object.keys(typeData).length > 0 &&
                        Object.values(typeData).some(val => val > 0);
                    if (hasValidSubclasses) {
                        filteredCombinedData[type] = typeData;
                    }
                });

                const wasteTypes = Object.keys(filteredCombinedData);
                const subClasses = Array.from(
                    new Set(wasteTypes.flatMap(type => Object.keys(filteredCombinedData[type] || {})))
                );

                const formattedData = wasteTypes.map(type => {
                    const typeData = filteredCombinedData[type] || {};
                    const total    = Object.values(typeData).reduce((sum, val) => sum + val, 0);

                    if (total === 0) return null;

                    const subClassValues = {};
                    const entries = Object.keys(typeData);

                    entries.forEach((subClass) => {
                        subClassValues[subClass] = total > 0 ? (typeData[subClass] / total) * 100 : 0;
                    });

                    // Force last item so percentages sum to exactly 100
                    const assignedTotal = entries.slice(0, -1).reduce((sum, key) => sum + subClassValues[key], 0);
                    subClassValues[entries[entries.length - 1]] = 100 - assignedTotal;

                    return {
                        name: type,
                        ...subClasses.reduce((acc, subClass) => ({
                            ...acc,
                            [subClass]: subClassValues[subClass] || 0
                        }), {})
                    };
                }).filter(item => item !== null);

                if (formattedData.length > 0) {
                    setProcessedData(formattedData);
                    setCategories(wasteTypes);
                    setHasData(true);
                } else {
                    setProcessedData([]);
                    setCategories([]);
                    setHasData(false);
                }

            } catch (err) {
                console.error('Data fetch error:', err);
                if (err.response?.status === 404 || err.message?.includes('No waste data found')) {
                    setProcessedData([]);
                    setCategories([]);
                    setHasData(false);
                    setError(null);
                } else {
                    setProcessedData([]);
                    setCategories([]);
                    setHasData(false);
                    setError(err.message);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dayvalue, id, t, isCustomDateRange, datevalue]);

    // ── Fetch waste type weights ─────────────────────────────────────────────
    React.useEffect(() => {
        const fetchWasteTypeWeights = async () => {
            try {
                let binIdentifiers;
                if (Array.isArray(id)) {
                    binIdentifiers = id;
                } else if (typeof id === 'string' && id.includes(',')) {
                    binIdentifiers = id.split(',');
                } else {
                    binIdentifiers = [id];
                }

                const bins = binIdentifiers.map(b => String(b).trim()).join(',');

                const timeRangeMap = {
                    'day':     'Day',
                    'week':    'Week',
                    'month':   'Month',
                    '3months': '3Month',
                    '6months': '6Month',
                    'year':    'Year'
                };

                const normalizedDayValue = dayvalue.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
                const timeRange          = timeRangeMap[normalizedDayValue] || 'Month';

                const currentMonth = new Date().getMonth() + 1;
                let season = 'summer';
                if (currentMonth >= 3  && currentMonth <= 5)  season = 'spring';
                else if (currentMonth >= 6  && currentMonth <= 8)  season = 'summer';
                else if (currentMonth >= 9  && currentMonth <= 11) season = 'autumn';
                else season = 'winter';

                const params = { bins, timeRange, season };

                if (isCustomDateRange && datevalue[0] && datevalue[0].startDate && datevalue[0].endDate) {
                    const rawStartDate = datevalue[0].startDate;
                    const rawEndDate   = datevalue[0].endDate;

                    params.start_date = rawStartDate instanceof Date
                        ? rawStartDate.toISOString()
                        : typeof rawStartDate === 'string'
                            ? rawStartDate
                            : new Date(rawStartDate).toISOString();

                    params.end_date = rawEndDate instanceof Date
                        ? rawEndDate.toISOString()
                        : typeof rawEndDate === 'string'
                            ? rawEndDate
                            : new Date(rawEndDate).toISOString();
                }

                const response = await axiosInstance.get('/others_page_waste_composition', { params });

                if (response.data) {
                    const weights = {};

                    if (response.data.paper !== undefined) {
                        weights['Paper/Cardboard']  = response.data.paper;
                        weights['Paper']            = response.data.paper;
                        weights[t('paperlabel')]    = response.data.paper;
                    }
                    if (response.data.pmd !== undefined) {
                        weights['PMD'] = response.data.pmd;
                    }
                    if (response.data.residual !== undefined) {
                        weights['Residual']         = response.data.residual;
                        weights['Rest']             = response.data.residual;
                        weights[t('residuallabel')] = response.data.residual;
                    }
                    if (response.data.organic !== undefined) {
                        weights['Organic']          = response.data.organic;
                        weights[t('organiclabel')]  = response.data.organic;
                    }
                    if (response.data.glass !== undefined) {
                        weights['Glass'] = response.data.glass;
                    }

                    setWasteTypeWeights(weights);
                }
            } catch (error) {
                console.error('Error fetching waste type weights:', error);
                setWasteTypeWeights({});
            }
        };

        fetchWasteTypeWeights();
    }, [id, dayvalue, t, isCustomDateRange, datevalue]);

    // ── Export CSV ───────────────────────────────────────────────────────────
    const handleExportCSV = () => {
        if (!processedData.length || !hasData) return;

        const subclassKeys = Object.keys(chartConfig);
        const headers      = ['Waste Type', 'Waste Type Weight (kg)', 'Subclass', 'Percentage (%)', 'Weight (kg)'];
        const rows         = [];
        let totalKgSum     = 0;

        processedData.forEach(item => {
            const wasteType       = item.name;
            const wasteTypeWeight = wasteTypeWeights[wasteType] || 0;

            subclassKeys.forEach(key => {
                const percentageValue = item[key] || 0;
                if (percentageValue > 0) {
                    const kgValue = (percentageValue / 100) * wasteTypeWeight;
                    totalKgSum += kgValue;
                    rows.push([
                        wasteType,
                        wasteTypeWeight.toFixed(3).replace('.', ','),
                        chartConfig[key].label,
                        percentageValue.toFixed(2),
                        kgValue.toFixed(3).replace('.', ',')
                    ]);
                }
            });
        });

        let csvContent = '# Subclass Waste Composition Export\n';
        csvContent += `# Export Date: ${new Date().toISOString().split('T')[0]}\n`;

        if (isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate) {
            const startD = datevalue[0].startDate instanceof Date ? datevalue[0].startDate : new Date(datevalue[0].startDate);
            const endD   = datevalue[0].endDate   instanceof Date ? datevalue[0].endDate   : new Date(datevalue[0].endDate);
            csvContent += `# Date Range: ${!isNaN(startD) ? startD.toISOString().split('T')[0] : 'Invalid'} to ${!isNaN(endD) ? endD.toISOString().split('T')[0] : 'Invalid'}\n`;
        } else {
            csvContent += `# Time Period: ${dayvalue}\n`;
        }

        csvContent += `# Total Waste Weight: ${String(wasteweight).replace('.', ',')} kg\n`;
        csvContent += `# Calculated Total from Subclasses: ${totalKgSum.toFixed(3).replace('.', ',')} kg\n`;
        csvContent += `# Total Objects: ${totalwasteproduceddata}\n`;
        csvContent += '#\n';
        csvContent += '# Note: Percentages show composition within each waste type (sums to 100% per type)\n\n';
        csvContent += headers.join(',') + '\n';
        csvContent += rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

        csvContent += '\n\n# Summary by Waste Type\n';
        csvContent += 'Waste Type,Waste Type Weight (kg),Total Percentage (%),Calculated Total Weight (kg)\n';
        processedData.forEach(item => {
            const wasteType       = item.name;
            const wasteTypeWeight = wasteTypeWeights[wasteType] || 0;
            const totalPercentage = subclassKeys.reduce((sum, key) => sum + (item[key] || 0), 0);
            const totalKg         = (totalPercentage / 100) * wasteTypeWeight;
            csvContent += `"${wasteType}","${wasteTypeWeight.toFixed(3).replace('.', ',')}","${totalPercentage.toFixed(2)}","${totalKg.toFixed(3).replace('.', ',')}"\n`;
        });

        csvContent += '\n\n# Contamination Analysis Helper\n';
        csvContent += '# Use this to identify misplaced items (subclasses in wrong waste type)\n';
        csvContent += 'Waste Type,Subclass,Weight (kg),Potential Issue\n';
        processedData.forEach(item => {
            const wasteType       = item.name;
            const wasteTypeWeight = wasteTypeWeights[wasteType] || 0;
            subclassKeys.forEach(key => {
                const percentageValue = item[key] || 0;
                if (percentageValue > 0) {
                    const kgValue       = (percentageValue / 100) * wasteTypeWeight;
                    const subclassName  = chartConfig[key].label.toLowerCase();
                    const wasteTypeName = wasteType.toLowerCase();

                    let issue = 'Correct placement';
                    if (wasteTypeName.includes('organic') && !subclassName.includes('organic') && !subclassName.includes('banana') && !subclassName.includes('apple') && !subclassName.includes('mandarin')) {
                        issue = 'POSSIBLE CONTAMINATION';
                    } else if (wasteTypeName.includes('pmd') && (subclassName.includes('paper') || subclassName.includes('organic'))) {
                        issue = 'POSSIBLE CONTAMINATION';
                    } else if (wasteTypeName.includes('paper') && !subclassName.includes('paper') && !subclassName.includes('cardboard') && !subclassName.includes('box') && !subclassName.includes('carton')) {
                        issue = 'POSSIBLE CONTAMINATION';
                    }

                    csvContent += `"${wasteType}","${chartConfig[key].label}","${kgValue.toFixed(3).replace('.', ',')}","${issue}"\n`;
                }
            });
        });

        const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
        const url  = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `subclass_waste_composition_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ── Export PNG ───────────────────────────────────────────────────────────
    const handleExportPNG = () => {
        if (!internalChartRef.current) return;

        const controls = internalChartRef.current.querySelector('.export-controls');
        if (controls) controls.style.display = 'none';

        import('html2canvas').then((html2canvas) => {
            html2canvas.default(internalChartRef.current, {
                scale:           2,
                logging:         false,
                useCORS:         true,
                backgroundColor: "#FFFFFF",
            }).then((canvas) => {
                const link    = document.createElement('a');
                link.download = `subclass_waste_composition_${new Date().toISOString().split('T')[0]}.png`;
                link.href     = canvas.toDataURL('image/png');
                link.click();
            }).finally(() => {
                if (controls) controls.style.display = '';
            });
        }).catch(err => {
            console.error('Failed to export as PNG:', err);
            if (controls) controls.style.display = '';
        });
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div
            ref={internalChartRef}
            className={cn(
                "lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section",
                className
            )}
            {...props}
        >
            {/* Header */}
            <div className="relative">
                <div className="flex justify-between items-center pb-1">
                    <div>
                        <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                            {chartTitle}
                        </h4>
                        {isCustomDateRange && datevalue[0]?.startDate && datevalue[0]?.endDate && (
                            <p className="text-sm text-muted-foreground mt-1">
                                {(() => {
                                    const sDate = datevalue[0].startDate instanceof Date ? datevalue[0].startDate : new Date(datevalue[0].startDate);
                                    const eDate = datevalue[0].endDate   instanceof Date ? datevalue[0].endDate   : new Date(datevalue[0].endDate);
                                    if (!isNaN(sDate) && !isNaN(eDate)) {
                                        return `${sDate.toLocaleDateString()} - ${eDate.toLocaleDateString()}`;
                                    }
                                    return '';
                                })()}
                            </p>
                        )}
                    </div>

                    <div className="flex items-center space-x-1 export-controls">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-muted-foreground hover:text-primary"
                                    disabled={!hasData || loading}
                                >
                                    <Download className="h-4 w-4 mr-1"/>
                                    <span className="text-sm">{t('export')}</span>
                                    <ChevronDown className="h-4 w-4 ml-1"/>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={handleExportCSV}>
                                    {t('downloadAsCSV')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={handleExportPNG}>
                                    {t('downloadAsPNG')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <TooltipProvider>
                            <UITooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground hover:text-primary h-8 w-8 p-0"
                                    >
                                        <CircleHelp className="h-4 w-4"/>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side="left">
                                    <p>
                                        This indicates the individual composition of your waste.<br/>
                                        Eg. Plastic bottles within PMD.<br/>
                                        If sub-classes do not match the main waste stream, this is a contamination event.<br/>
                                        <br/>
                                        <strong>Date Range:</strong> Use the calendar in the header to select a custom date range
                                    </p>
                                </TooltipContent>
                            </UITooltip>
                        </TooltipProvider>
                    </div>
                </div>

                <div className="flex items-center justify-center gap-2 mb-1 text-sm min-h-[1.5rem]">
                    {loading ? (
                        <div className="motion-safe:animate-pulse h-4 w-40 rounded bg-gray-200 dark:bg-slate-700" />
                    ) : hasData && totalwasteproduceddata > 0 && wasteweight > 0 && !error ? (
                        <>
                            <span className="font-semibold">
                                {totalwasteproduceddata} <span className="font-light">{t('objectslabel')}</span> | {String(wasteweight).replace('.', ',')}
                                <span className="font-light"> KG</span>
                            </span>
                            {percentagechangewastechart && percentagechangewastechart !== 0 && (
                                <span className={`text-${colorwastechart}-500 flex items-center justify-center text-xs font-semibold`}>
                                    {percentagechangewastechart}% {colorwastechart === "red" ?
                                    <AiOutlineArrowUp className="h-3 w-3"/> :
                                    <AiOutlineArrowDown className="h-3 w-3"/>}
                                </span>
                            )}
                        </>
                    ) : !hasData && !error ? (
                        <span className="font-semibold text-muted-foreground">
                            {t('noData')}
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Chart */}
            <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{width: '100%', height: '94%'}}>
                {loading ? (
                    // Skeleton — same shape as the loaded bar chart: y-axis
                    // stream labels (Organic/Paper/PMD/Residual), four
                    // horizontal stacked bars split into a few segments at
                    // varying widths, and the 0/30/60/100% x-axis ticks
                    // underneath.
                    <div className="flex flex-col h-full w-full motion-safe:animate-pulse" aria-busy="true">
                        <span className="sr-only" role="status">{t('loading', 'Loading…')}</span>
                        <div className="flex-1 flex flex-col justify-around pr-[70px]">
                            {[
                                [55, 20, 15, 10],
                                [10, 35, 20, 25, 10],
                                [8, 45, 12, 30, 5],
                                [12, 18, 25, 45],
                            ].map((segments, row) => (
                                <div key={row} className="flex items-center gap-2">
                                    <div className="w-14 h-3 rounded bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
                                    <div className="flex-1 h-[30px] flex rounded overflow-hidden">
                                        {segments.map((w, i) => (
                                            <div
                                                key={i}
                                                className="h-full bg-gray-200 dark:bg-slate-700"
                                                style={{
                                                    width: `${w}%`,
                                                    opacity: 0.5 + (i % 3) * 0.15,
                                                    borderRight: i < segments.length - 1 ? '1px solid rgba(255,255,255,0.4)' : 'none',
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-between pl-16 pr-[70px] pt-2">
                            {['0%', '30%', '60%', '100%'].map((label, i) => (
                                <div key={i} className="h-2 w-8 rounded bg-gray-200 dark:bg-slate-700" />
                            ))}
                        </div>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-full">
                        <div className="text-muted-foreground text-center">
                            <p className="mb-2 text-lg">{t('errorLoadingData')}</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    </div>
                ) : (
                    <ChartContainer config={chartConfig} className="h-full w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={hasData ? processedData : []}
                                layout="vertical"
                                margin={{top: 1, right: 70, left: 10, bottom: 20}}
                                barSize={30}
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false}/>
                                <XAxis
                                    type="number"
                                    domain={[0, 100]}
                                    tickFormatter={(value) => `${value.toFixed(1)}%`}
                                />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    width={60}
                                    tick={{fontSize: 12}}
                                    axisLine={false}
                                    tickLine={false}
                                    tickMargin={10}
                                />
                                <Tooltip
                                    content={({active, payload, label}) => {
                                        if (!active || !payload || !hasData) return null;

                                        const filteredAndSortedPayload = payload
                                            .filter(item => item.value > 0)
                                            .map(item => ({
                                                ...item,
                                                color: chartConfig[item.dataKey]?.color || item.color,
                                                name:  chartConfig[item.dataKey]?.label || item.name
                                            }))
                                            .sort((a, b) => b.value - a.value);

                                        return (
                                            <div className="rounded-lg border bg-background p-3 shadow-md">
                                                <div className="font-medium mb-2">{`${t('wastetype')}: ${label}`}</div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {filteredAndSortedPayload.map((item, index) => (
                                                        <React.Fragment key={`item-${index}`}>
                                                            <div className="flex items-center">
                                                                <div className="mr-1 h-2 w-2 rounded-full"
                                                                     style={{backgroundColor: item.color}}/>
                                                                <div>{item.name}</div>
                                                            </div>
                                                            <div className="text-right font-medium">
                                                                {item.value.toFixed(1)}%
                                                            </div>
                                                        </React.Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                                {hasData && Object.keys(chartConfig).map((key) => (
                                    <Bar
                                        key={key}
                                        dataKey={key}
                                        stackId="a"
                                        fill={chartConfig[key].color}
                                        name={chartConfig[key].label}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                )}
            </div>
        </div>
    );
});

SubclassDisplayChart.displayName = "SubclassDisplayChart";